import { DensityMode } from "@/contexts/AdaptiveViewportContext";
import { UnifiedAvatar } from "@/components/shared/UnifiedAvatar";

interface SocialProofAvatar {
  id: string;
  username: string;
  photo_url: string | null;
}

interface SocialProofBarProps {
  subscriberCount?: number;
  topAvatars?: SocialProofAvatar[];
  isLoading?: boolean;
  className?: string;
  densityMode?: DensityMode;
}

// Fallback gradients para quando não há fotos suficientes
const FALLBACK_GRADIENTS = [
  "bg-gradient-to-br from-luxury-pinkBright to-purple-600",
  "bg-gradient-to-br from-blue-500 to-purple-600",
  "bg-gradient-to-br from-pink-500 to-luxury-pinkBright",
];

export const SocialProofBar = ({ 
  subscriberCount = 2847,
  topAvatars = [],
  isLoading = false,
  className = "",
  densityMode = 'expanded'
}: SocialProofBarProps) => {
  // Modo compacto: versão ultra-mini
  if (densityMode === 'compact') {
    return (
      <div className={`text-center ${className}`}>
        <span className="text-[10px] text-white/50">
          ⭐ {subscriberCount.toLocaleString('pt-BR')}+ sponsors
        </span>
      </div>
    );
  }

  // Modo normal: apenas texto
  if (densityMode === 'normal') {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <p className="text-xs text-white/70">
          ⭐ <span className="font-bold text-white">{subscriberCount.toLocaleString('pt-BR')}+</span> sponsors confiam
        </p>
      </div>
    );
  }

  // Renderizar avatares (reais ou placeholders)
  const renderAvatars = () => {
    if (isLoading) {
      // Skeleton loading
      return (
        <>
          {[0, 1, 2].map((i) => (
            <div 
              key={i} 
              className="w-8 h-8 rounded-full bg-white/10 border-2 border-black animate-pulse" 
            />
          ))}
        </>
      );
    }

    // Se temos avatares reais, usá-los
    if (topAvatars.length > 0) {
      // Preencher até 3 slots (reais + placeholders)
      const avatarsToShow = [...topAvatars];
      const placeholdersNeeded = 3 - avatarsToShow.length;

      return (
        <>
          {avatarsToShow.map((avatar) => (
            <UnifiedAvatar
              key={avatar.id}
              src={avatar.photo_url || undefined}
              alt={avatar.username || 'Sponsor'}
              size="micro"
              fallback={avatar.username?.charAt(0)?.toUpperCase() || '?'}
              className="border-2 border-gray-800"
            />
          ))}
          {/* Placeholders para completar 3 - mesmo tamanho que UnifiedAvatar micro */}
          {Array.from({ length: placeholdersNeeded }).map((_, i) => (
            <div 
              key={`placeholder-${i}`} 
              className={`w-8 h-8 rounded-full overflow-hidden ${FALLBACK_GRADIENTS[topAvatars.length + i] || FALLBACK_GRADIENTS[0]} border-2 border-gray-800`} 
            />
          ))}
        </>
      );
    }

    // Fallback: todos placeholders com gradientes - mesmo tamanho que UnifiedAvatar micro
    return FALLBACK_GRADIENTS.map((gradient, i) => (
      <div 
        key={i} 
        className={`w-8 h-8 rounded-full overflow-hidden ${gradient} border-2 border-gray-800`} 
      />
    ));
  };

  // Modo expandido: avatars + texto
  return (
    <div className={`flex items-center justify-center gap-2 ${className}`}>
      {/* Avatar stack */}
      <div className="flex -space-x-2">
        {renderAvatars()}
      </div>
      
      {/* Count text */}
      <p className="text-sm text-white/70">
        <span className="font-bold text-white">{subscriberCount.toLocaleString('pt-BR')}+</span> sponsors confiam
      </p>
    </div>
  );
};
